import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    // If auth fails, let the request proceed without token
    // API Gateway will return 401
  }
  return config;
});

// Response interceptor for standard error handling
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    const message = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

// --- Submissions ---
export const submissions = {
  list: (params) => apiClient.get('/submissions', { params }),
  get: (id) => apiClient.get(`/submissions/${id}`),
  create: (data) => apiClient.post('/submissions', data),
  update: (id, data) => apiClient.put(`/submissions/${id}`, data),
  assign: (id, data) => apiClient.post(`/submissions/${id}/assign`, data),
  getMissingData: (id) => apiClient.get(`/submissions/${id}/missing-data`),
  delete: (id) => apiClient.delete(`/submissions/${id}`),
  dismiss: (id) => apiClient.post(`/submissions/${id}/dismiss`),
};

// --- Underwriting ---
export const underwriting = {
  getCanvas: (submissionId) => apiClient.get(`/underwriting/${submissionId}/canvas`),
  saveAssessment: (submissionId, data) => apiClient.post(`/underwriting/${submissionId}/assessment`, data),
  updateAssessment: (submissionId, data) => apiClient.put(`/underwriting/${submissionId}/assessment`, data),
  makeDecision: (submissionId, data) => apiClient.post(`/underwriting/${submissionId}/decision`, data),
  rescore: (submissionId) => apiClient.post(`/underwriting/${submissionId}/rescore`),
  getSimilarRisks: (submissionId) => apiClient.get(`/underwriting/${submissionId}/similar-risks`),
};

// --- Accounts ---
export const accounts = {
  list: (params) => apiClient.get('/accounts', { params }),
  get: (id) => apiClient.get(`/accounts/${id}`),
  create: (data) => apiClient.post('/accounts', data),
  getMovement: (id) => apiClient.get(`/accounts/${id}/movement`),
};

// --- Brokers ---
export const brokers = {
  list: (params) => apiClient.get('/brokers', { params }),
  get: (id) => apiClient.get(`/brokers/${id}`),
  create: (data) => apiClient.post('/brokers', data),
  update: (id, data) => apiClient.put(`/brokers/${id}`, data),
  delete: (id) => apiClient.delete(`/brokers/${id}`),
  getChildren: (id) => apiClient.get(`/brokers/${id}/children`),
  getDashboard: () => apiClient.get('/brokers/dashboard'),
  lookupByEmail: (email) => apiClient.get('/brokers/lookup-by-email', { params: { email } }),
  listContacts: (id) => apiClient.get(`/brokers/${id}/contacts`),
  addContact: (id, data) => apiClient.post(`/brokers/${id}/contacts`, data),
  updateContact: (id, contactId, data) => apiClient.put(`/brokers/${id}/contacts/${contactId}`, data),
  deleteContact: (id, contactId) => apiClient.delete(`/brokers/${id}/contacts/${contactId}`),
  listActivities: (id) => apiClient.get(`/brokers/${id}/activities`),
  logActivity: (id, data) => apiClient.post(`/brokers/${id}/activities`, data),
  listSubmissions: (id) => apiClient.get(`/brokers/${id}/submissions`),
  listAccounts: (id) => apiClient.get(`/brokers/${id}/accounts`),
};

// --- Products ---
export const products = {
  list: (params) => apiClient.get('/products', { params }),
  get: (id) => apiClient.get(`/products/${id}`),
  create: (data) => apiClient.post('/products', data),
  update: (id, data) => apiClient.put(`/products/${id}`, data),
  delete: (id) => apiClient.delete(`/products/${id}`),
  listIndustryCodes: (id, params) => apiClient.get(`/products/${id}/industry-codes`, { params }),
  importIndustryCodes: (id, data) => apiClient.post(`/products/${id}/industry-codes`, data),
  updateIndustryCode: (id, naicsCode, data) => apiClient.put(`/products/${id}/industry-codes/${naicsCode}`, data),
  listRiskTags: (id, params) => apiClient.get(`/products/${id}/risk-tags`, { params }),
  addRiskTag: (id, data) => apiClient.post(`/products/${id}/risk-tags`, data),
  updateRiskTag: (id, tagId, data) => apiClient.put(`/products/${id}/risk-tags/${tagId}`, data),
  deleteRiskTag: (id, tagId) => apiClient.delete(`/products/${id}/risk-tags/${tagId}`),
  getThresholds: (id) => apiClient.get(`/products/${id}/thresholds`),
  updateThresholds: (id, data) => apiClient.put(`/products/${id}/thresholds`, data),
};

// --- Quotes ---
export const quotes = {
  create: (data) => apiClient.post('/quotes', data),
  get: (id) => apiClient.get(`/quotes/${id}`),
  deliver: (id) => apiClient.post(`/quotes/${id}/deliver`),
  accept: (id) => apiClient.post(`/quotes/${id}/accept`),
};

// --- Policies ---
export const policies = {
  list: (params) => apiClient.get('/policies', { params }),
  get: (id) => apiClient.get(`/policies/${id}`),
};

// --- Claims ---
export const claims = {
  list: (params) => apiClient.get('/claims', { params }),
  create: (data) => apiClient.post('/claims', data),
};

// --- Leadership ---
export const leadership = {
  getDashboard: () => apiClient.get('/leadership/dashboard'),
  getOverrides: () => apiClient.get('/leadership/overrides'),
  getAccumulation: () => apiClient.get('/leadership/accumulation'),
  getCapacityDashboard: (providerId) => apiClient.get(`/leadership/capacity/${providerId}`),
};

// --- Capacity Partners ---
export const capacityPartners = {
  getDashboard: (providerId) => apiClient.get(`/capacity-partners/${providerId}/dashboard`),
  getMetrics: (providerId, params) => apiClient.get(`/capacity-partners/${providerId}/metrics`, { params }),
  list: () => apiClient.get('/capacity-partners'),
};

// --- Communications ---
export const communications = {
  send: (data) => apiClient.post('/communications', data),
  getByAccount: (accountId) => apiClient.get(`/communications/${accountId}`),
};

// --- Documents ---
export const documents = {
  upload: (data) => apiClient.post('/documents/upload', data),
  process: (id) => apiClient.post(`/documents/${id}/process`),
  get: (id) => apiClient.get(`/documents/${id}`),
  delete: (id) => apiClient.delete(`/documents/${id}`),
  getBySubmission: (submissionId) => apiClient.get(`/documents/submission/${submissionId}`),
};

// --- Approvals ---
export const approvals = {
  listThresholds: () => apiClient.get('/approvals/thresholds'),
  createThreshold: (data) => apiClient.post('/approvals/thresholds', data),
  getProductThresholds: (productId) => apiClient.get(`/approvals/thresholds/${productId}`),
  deleteThreshold: (productId, authorityLevel) => apiClient.delete(`/approvals/thresholds/${productId}/${authorityLevel}`),
  listOverrides: () => apiClient.get('/approvals/overrides'),
  recordOverride: (data) => apiClient.post('/approvals/overrides', data),
};

// --- Users ---
export const users = {
  list: (params) => apiClient.get('/users', { params }),
  get: (id) => apiClient.get(`/users/${id}`),
  create: (data) => apiClient.post('/users', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  getWorkload: (id) => apiClient.get(`/users/${id}/workload`),
};

// --- Underwriting Agent ---
export const agent = {
  chat: (data) => apiClient.post('/agent/chat', data),
  poll: (requestId) => apiClient.get(`/agent/chat/${requestId}`),
  listConversations: () => apiClient.get('/agent/conversations'),
  getConversation: (sessionId) => apiClient.get(`/agent/conversations/${sessionId}`),
  deleteConversation: (sessionId) => apiClient.delete(`/agent/conversations/${sessionId}`),
};

export default apiClient;
